import {
  addDays,
  addMonths,
  addYears,
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  endOfWeek,
  format,
  formatDistance,
  isAfter as isAfterDateFns,
  isBefore as isBeforeDateFns,
  isEqual,
  isFuture as isFutureDateFns,
  isPast as isPastDateFns,
  isSameDay,
  isSameMonth,
  isSameYear,
  isToday as isTodayDateFns,
  isValid as isValidDateFns,
  isWeekend as isWeekendDateFns,
  isWithinInterval,
  parse,
  parseISO as parseISODateFns,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import {
  getTimezoneOffset as getTimezoneOffsetTz,
  utcToZonedTime,
  zonedTimeToUtc,
} from "date-fns-tz";

import { DEFAULT_CONFIG } from "./constants";
import { DateParsingError, DateValidationError } from "./errors";
import {
  DateRange,
  DateServiceConfig,
  DateUnit,
  DateService as IDateService,
} from "./types";

export class DateService implements IDateService {
  private config: DateServiceConfig;
  private mockedNow: Date | null = null;
  private readonly cache: Map<string, any> = new Map();

  constructor(config: Partial<DateServiceConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // Core parsing method
  parseApiDate(dateString: string): Date {
    try {
      if (!dateString) {
        throw new DateParsingError("Date string is required");
      }

      const cleaned = this.sanitizeDateString(dateString);

      let date: Date;

      if (cleaned.includes("T")) {
        // prettier-ignore
        date = parse(cleaned, 'yyyy-MM-dd\'T\'HH:mm:ss', new Date());
      } else {
        date = parse(cleaned, "yyyy-MM-dd", new Date());
      }

      if (!isValidDateFns(date)) {
        date = parseISODateFns(cleaned);

        if (!isValidDateFns(date)) {
          throw new DateParsingError(`Invalid date format: ${dateString}`);
        }
      }

      return date;
    } catch (error) {
      this.handleError(error, "parseApiDate", { dateString });
      throw error;
    }
  }

  parseApiDateTime(dateTimeString: string): Date {
    try {
      if (!dateTimeString) {
        throw new DateParsingError("DateTime string is required");
      }

      const cleaned = this.sanitizeDateString(dateTimeString);
      // prettier-ignore
      const date = parse(cleaned, 'yyyy-MM-dd\'T\'HH:mm:ss', new Date());

      if (!isValidDateFns(date)) {
        // prettier-ignore
        const dateWithMs = parse(cleaned, 'yyyy-MM-dd\'T\'HH:mm:ss.SSS', new Date());

        if (!isValidDateFns(dateWithMs)) {
          throw new DateParsingError(
            `Invalid datetime format: ${dateTimeString}`
          );
        }

        return dateWithMs;
      }

      return date;
    } catch (error) {
      this.handleError(error, "parseApiDateTime", { dateTimeString });
      throw error;
    }
  }

  parseISO(isoString: string): Date {
    const date = parseISODateFns(isoString);
    if (!isValidDateFns(date)) {
      throw new DateParsingError(`Invalid ISO date format: ${isoString}`);
    }
    return date;
  }

  parseUserInput(input: string, formatString?: string): Date {
    const date = formatString
      ? parse(input, formatString, new Date())
      : parse(input, "MM/dd/yyyy", new Date()); // Default US format

    if (!isValidDateFns(date)) {
      throw new DateParsingError(`Invalid user input date format: ${input}`);
    }

    return date;
  }

  formatForApi(date: Date): string {
    try {
      this.validateDate(date);
      return format(date, "yyyy-MM-dd");
    } catch (error) {
      this.handleError(error, "formatForApi", { date });
      throw error;
    }
  }

  formatForApiDateTime(date: Date): string {
    try {
      this.validateDate(date);
      // prettier-ignore
      return format(date, 'yyyy-MM-dd\'T\'HH:mm:ss');
    } catch (error) {
      this.handleError(error, "formatForApiDateTime", { date });
      throw error;
    }
  }

  formatForDisplay(date: Date, formatString: string = "MM/dd/yyyy"): string {
    try {
      this.validateDate(date);
      const cacheKey = `display:${date.getTime()}:${formatString}`;
      if (this.cache.has(cacheKey)) {
        return this.cache.get(cacheKey);
      }

      let result: string;

      // Map common format names to date-fns format strings
      const formatMap: Record<string, string> = {
        "MM/DD/YYYY": "MM/dd/yyyy",
        "DD/MM/YYYY": "dd/MM/yyyy",
        "YYYY-MM-DD": "yyyy-MM-dd",
        "MMM DD, YYYY": "MMM dd, yyyy",
        "MMM D, YYYY": "MMM d, yyyy",
        "MMMM DD, YYYY": "MMMM dd, yyyy",
        "MMMM D, YYYY": "MMMM d, yyyy",
        "MMMM YYYY": "MMMM yyyy",
        "MMM YYYY": "MMM yyyy",
        YYYY: "yyyy",
        full: "PPPP",
        long: "PPP",
        medium: "PP",
        short: "P",
      };

      const actualFormat = formatMap[formatString] || formatString;

      if (formatString === "relative") {
        result = this.formatRelative(date);
      } else {
        result = format(date, actualFormat, {
          locale: this.config.locale,
        });
      }

      this.cache.set(cacheKey, result);
      return result;
    } catch (error) {
      this.handleError(error, "formatForDisplay", { date, formatString });
      throw error;
    }
  }

  formatRelative(date: Date, baseDate: Date = new Date()): string {
    return formatDistance(date, baseDate, {
      addSuffix: true,
      locale: this.config.locale,
    });
  }

  // Timezone operations
  toUTC(date: Date): Date {
    return zonedTimeToUtc(
      date,
      this.config.defaultTimezone || "America/Chicago"
    );
  }

  toLocal(date: Date): Date {
    return utcToZonedTime(
      date,
      this.config.defaultTimezone || "America/Chicago"
    );
  }

  toTimezone(date: Date, timezone: string): Date {
    return utcToZonedTime(date, timezone);
  }

  getTimezoneOffset(date: Date, timezone?: string): number {
    const tz = timezone || this.config.defaultTimezone || "America/Chicago";
    return getTimezoneOffsetTz(tz, date);
  }

  // Validation methods
  isValid(date: any): boolean {
    return date instanceof Date && isValidDateFns(date);
  }

  isBusinessDay(date: Date): boolean {
    return !this.isWeekend(date) && !this.isHoliday(date);
  }

  isHoliday(date: Date): boolean {
    return (
      (this.config.holidays &&
        this.config.holidays.some((holiday) => isSameDay(date, holiday))) ||
      false
    );
  }

  isWeekend(date: Date): boolean {
    return isWeekendDateFns(date);
  }

  isInRange(date: Date, start: Date, end: Date): boolean {
    return isWithinInterval(date, { start, end });
  }

  isFuture(date: Date): boolean {
    return isFutureDateFns(date);
  }

  isPast(date: Date): boolean {
    return isPastDateFns(date);
  }

  isToday(date: Date): boolean {
    return isTodayDateFns(date);
  }

  // Comparison methods
  isBefore(date1: Date, date2: Date): boolean {
    return isBeforeDateFns(date1, date2);
  }

  isAfter(date1: Date, date2: Date): boolean {
    return isAfterDateFns(date1, date2);
  }

  isSame(date1: Date, date2: Date, unit: DateUnit = "day"): boolean {
    switch (unit) {
      case "day":
        return isSameDay(date1, date2);
      case "month":
        return isSameMonth(date1, date2);
      case "year":
        return isSameYear(date1, date2);
      case "hour":
        return date1.getHours() === date2.getHours() && isSameDay(date1, date2);
      case "minute":
        return (
          date1.getMinutes() === date2.getMinutes() &&
          date1.getHours() === date2.getHours() &&
          isSameDay(date1, date2)
        );
      default:
        return isEqual(date1, date2);
    }
  }

  diff(date1: Date, date2: Date, unit: DateUnit = "day"): number {
    switch (unit) {
      case "day":
        return differenceInDays(date1, date2);
      case "month":
        return differenceInMonths(date1, date2);
      case "year":
        return differenceInYears(date1, date2);
      default:
        return differenceInDays(date1, date2);
    }
  }

  // Manipulation methods
  add(date: Date, amount: number, unit: DateUnit): Date {
    switch (unit) {
      case "day":
        return addDays(date, amount);
      case "month":
        return addMonths(date, amount);
      case "year":
        return addYears(date, amount);
      default:
        return addDays(date, amount);
    }
  }

  subtract(date: Date, amount: number, unit: DateUnit): Date {
    switch (unit) {
      case "day":
        return subDays(date, amount);
      case "month":
        return subMonths(date, amount);
      case "year":
        return subYears(date, amount);
      default:
        return subDays(date, amount);
    }
  }

  startOf(date: Date, unit: DateUnit): Date {
    switch (unit) {
      case "day":
        return startOfDay(date);
      case "week": {
        const zoned = this.toLocal(date);
        return startOfWeek(zoned, { weekStartsOn: 1 });
      }
      case "month":
        return startOfMonth(date);
      case "year":
        return startOfYear(date);
      case "quarter":
        return startOfQuarter(date);
      default:
        return startOfDay(date);
    }
  }

  endOf(date: Date, unit: DateUnit): Date {
    switch (unit) {
      case "day":
        return endOfDay(date);
      case "week": {
        const zoned = this.toLocal(date);
        return endOfWeek(zoned, { weekStartsOn: 1 });
      }
      case "month":
        return endOfMonth(date);
      case "year":
        return endOfYear(date);
      case "quarter":
        return endOfQuarter(date);
      default:
        return endOfDay(date);
    }
  }

  // Business logic methods using date-fns
  getNextBusinessDay(date: Date): Date {
    let nextDay = addDays(date, 1);
    while (!this.isBusinessDay(nextDay)) {
      nextDay = addDays(nextDay, 1);
    }
    return nextDay;
  }

  getPreviousBusinessDay(date: Date): Date {
    let prevDay = subDays(date, 1);
    while (!this.isBusinessDay(prevDay)) {
      prevDay = subDays(prevDay, 1);
    }

    return prevDay;
  }

  addBusinessDays(date: Date, days: number): Date {
    let result = date;
    let daysToAdd = Math.abs(days);
    const direction = days > 0 ? 1 : -1;

    while (daysToAdd > 0) {
      result = addDays(result, direction);
      if (this.isBusinessDay(result)) {
        daysToAdd--;
      }
    }

    return result;
  }

  // Range operations
  createRange(start: Date, end: Date): DateRange {
    return {
      start,
      end,
      isInclusive: true,
    };
  }

  getDaysInRange(range: DateRange): Date[] {
    const days: Date[] = [];
    let current = range.start;
    const endDate = range.isInclusive ? range.end : subDays(range.end, 1);

    while (!isAfterDateFns(current, endDate)) {
      days.push(current);
      current = addDays(current, 1);
    }

    return days;
  }

  getBusinessDaysInRange(range: DateRange): Date[] {
    return this.getDaysInRange(range).filter((day) => this.isBusinessDay(day));
  }

  clone(date: Date): Date {
    return new Date(date.getTime());
  }

  // Utility methods
  now(): Date {
    return this.mockedNow ? new Date(this.mockedNow) : new Date();
  }

  today(): Date {
    return startOfDay(this.now());
  }

  tomorrow(): Date {
    return addDays(this.today(), 1);
  }

  yesterday(): Date {
    return subDays(this.today(), 1);
  }

  // Configuration methods
  configure(config: Partial<DateServiceConfig>): void {
    this.config = { ...this.config, ...config };
  }

  getConfig(): DateServiceConfig {
    return { ...this.config };
  }

  private validateDate(date: Date): void {
    if (!this.isValid(date)) {
      throw new DateValidationError("Invalid date provided");
    }
  }

  // Utility methods
  private sanitizeDateString(dateString: string): string {
    // Remove any timezone indicators and normalize
    return dateString
      .replace(/Z$/i, "")
      .replace(/[+-]\d{2}:\d{2}$/, "")
      .trim();
  }

  private handleError(error: unknown, method: string, context: unknown): void {
    if (this.config.logger) {
      this.config.logger.trace(`[DateService.${method}] Error: ${error}`, {
        level: "Error",
        sentryLogging: true,
        extra: {
          error,
          context,
        },
      });
    }
  }

  // Testing utilities
  mockNow(date: Date): void {
    this.mockedNow = date;
  }

  resetMock(): void {
    this.mockedNow = null;
    this.cache.clear();
  }
}

// Singleton instance for app-wide usage
export const dateService = new DateService();
